import React, {
  useState,
  useLayoutEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react'
import { Link } from 'react-router-dom'
import ProfileDialog from '../ProfileDialog/ProfileDialog'
import SideMenu from '../SideMenu/SideMenu'
import Button from '../Button/Button'
import { useRouter, useOutsideHandle } from '../../hooks'
import { useSelector } from 'react-redux'
import CSSTransition from 'react-transition-group/CSSTransition'
import { detectAgent } from '../../utils'

import logo from '../../assets/sushiroll_logo.svg'
import icSearch from '../../assets/ic-search.svg'
import icUser from '../../assets/ic-user.svg'
import icSideMenu from '../../assets/ic-side-menu.svg'
import icClose from '../../assets/ic-close.svg'
import ValidateSessionNotification from '../Notification/ValidateSessionNotification'
import styles from './Header.module.css'
import ToastNotification from '../Notification/toast'
import MobileProfileDialog from '../ProfileDialog/MobileProfileDialog'

const pageOrderCondition = ['movies', 'series', 'animation']
const sortPage = (page1, page2) => {
  for (let i = 0; i < pageOrderCondition.length; i++) {
    const item = pageOrderCondition[i]
    if (page1 === item) return -1
    if (page2 === item) return 1
    continue
  }
}

const Header = () => {
  const { user, sessionStatus } = useSelector((state) => state.auth)
  const pageData = useSelector((state) => state.page.data)
  const authenticated = !!user
  const profile = user && user.profile
  const [showProfile, toggleDialog] = useState(false)
  const [showSideMenu, toggleSideMenu] = useState(false)
  const [showSearchField, toggleSearch] = useState(false)
  const dialogRef = useRef()
  const formRef = useRef()
  const searchRef = useRef()
  const toggleProfile = () => {
    toggleDialog((open) => !open)
  }
  const router = useRouter()
  const menu = useMemo(() => {
    return pageData
      .filter((item) => item.friendlyId !== 'home')
      .sort((firstPage, secondPage) => {
        const { friendlyId: firstId } = firstPage
        const { friendlyId: secondId } = secondPage
        const sortValue = sortPage(firstId, secondId)
        return sortValue || 0
      })
      .map((item) => {
        const name = item.titleLocalized
        const friendlyId = item.friendlyId
        return {
          name,
          path: `/${friendlyId}`,
        }
      })
  }, [pageData])
  const isMobile = detectAgent('mobile')
  const handleClickOutsideDialog = useCallback(
    (target) => {
      //ignore user-profile-icon to use its onClick func
      if (target.dataset.iconName === 'user-profile-icon') {
        return
      }
      if (showProfile) {
        toggleProfile()
      }
    },
    [showProfile]
  )

  const handleClickOutsideSearchField = useCallback(() => {
    if (showSearchField) {
      toggleSearch(!showSearchField)
    }
  }, [showSearchField])

  useOutsideHandle(dialogRef, handleClickOutsideDialog)
  useOutsideHandle(formRef, handleClickOutsideSearchField)

  useLayoutEffect(() => {
    if (showSearchField) {
      searchRef.current.focus()
    }
  }, [showSearchField])

  const handleClickSearch = () => {
    toggleSearch(!showSearchField)
  }

  const handleSubmitSearch = (e) => {
    e.preventDefault()
    router.history.push(`/search?keyword=${searchRef.current.value}`)
  }

  return (
    <div className={styles.header}>
      <ToastNotification />
      {/*<SideMenu open={showSideMenu} menu={menu} />*/}
      {/*<img*/}
      {/*  className={`${styles['side-menu-toggle']} show-mobile`}*/}
      {/*  src={showSideMenu ? icClose : icSideMenu}*/}
      {/*  alt='side-menu-toggle'*/}
      {/*  onClick={() => toggleSideMenu(!showSideMenu)}*/}
      {/*/>*/}
      {isMobile && showSearchField ? null : (
        <Link to='/'>
          <img src={logo} alt='logo' className={styles.logo} />
        </Link>
      )}
      <ul className={`${styles['menu']} hide-mobile`}>
        {menu.map((item) => {
          const selected = router.location.pathname === item.path
          return (
            <li key={item.name} className={selected ? styles['selected'] : ''}>
              <Link to={item.path}>{item.name}</Link>
            </li>
          )
        })}
        {menu.length > 0 && (
          <li
            className={
              router.location.pathname === '/live' ? styles['selected'] : ''
            }
          >
            <Link to='/live'>Live</Link>
          </li>
        )}
      </ul>

      <ul className={styles['sub-menu']}>
        <li
          className={
            showSearchField
              ? `${styles.search} ${styles['search-opened']}`
              : styles.search
          }
        >
          <img
            src={icSearch}
            alt='icon'
            onClick={handleClickSearch}
            className={styles['search-img']}
          />
          <CSSTransition
            in={showSearchField}
            timeout={400}
            classNames='expand-rtl'
            unmountOnExit
          >
            <form
              className={styles['search-form']}
              ref={formRef}
              onSubmit={handleSubmitSearch}
            >
              <i
                className={styles['search-media-icon']}
                onClick={handleSubmitSearch}
              />
              <input
                ref={searchRef}
                placeholder='Search Term'
                className={styles['search-media-field']}
              />
            </form>
          </CSSTransition>
        </li>
        {isMobile && showSearchField ? null : authenticated ? (
          <li>
            <img
              src={profile && profile.avatar ? profile.avatar : icUser}
              width={24}
              height={24}
              className={styles['user-profile-icon']}
              alt='icon'
              data-icon-name='user-profile-icon'
              onClick={toggleProfile}
            />
          </li>
        ) : (
          <>
            <li>
              <Button
                small
                width='80px'
                onClick={() => router.history.push('/login')}
              >
                Login
              </Button>
            </li>
            <li>
              <Button
                small
                width='80px'
                outlined
                onClick={() => router.history.push('/register')}
              >
                Join
              </Button>
            </li>
          </>
        )}
        {isMobile && showSearchField && (
          <li>
            <Button
              small
              width='80px'
              style={{ margin: '0px' }}
              outlined
              onClick={() => toggleSearch()}
            >
              Cancel
            </Button>
          </li>
        )}
      </ul>

      <CSSTransition
        in={showProfile}
        timeout={300}
        classNames='fade'
        unmountOnExit
      >
        <div className={styles['profile-dialog-wrapper']} ref={dialogRef}>
          {isMobile ? (
            <MobileProfileDialog closeDialog={toggleProfile} />
          ) : (
            <ProfileDialog />
          )}
        </div>
      </CSSTransition>

      <ValidateSessionNotification open={sessionStatus === 'failed'} />
    </div>
  )
}

export default Header
